import { Injectable } from '@angular/core';
// Import directo al archivo (no el barril): `AuthService` inyecta este vigía para `reset()`, así que
// aquí nunca se inyecta `AuthService` (ciclo de DI). `UserService` solo depende de `HttpClient`.
import { UserService } from '@modules/auth/services/user.service';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';

import { CreditLine, Credito } from '../models';
import { isLineDoneSeen, markLineDoneSeen, readKnownLineLevel, writeKnownLineLevel } from '../utils/line-modal-views.util';
import { isLineWrittenOff } from '../utils/line-presentation.util';
import { CreditosService } from './creditos.service';

/** Lo que monta `AppComponent` con `fina-line-done-modal`. */
export interface LineMilestoneModal {
    line: CreditLine;
    /** Solo el nivel: el modal compara `line.level` con este para decidir «Subiste» o «Pagaste». */
    credito: Pick<Credito, 'level'>;
}

/** Motivos por los que otro modal pide que el vigía no abra el suyo encima. */
export type LineMilestoneHold = 'welcome' | 'celebration';

/**
 * Vigía global de Fina Presta (FINA-3378): abre «Pagaste tu préstamo» / «Subiste a Nivel N» en
 * cualquier módulo, una sola vez por crédito, apenas `GET /creditos/current` lo trae `completed`.
 *
 * - Es `providedIn: 'root'` y NO va en el array `services` de `CreditosModule`: si no, cada injector
 *   lazy tendría su instancia y `creditos-main` / el Home hablarían con una que `AppComponent` no
 *   escucha.
 * - Lee `/current` (tope de 5 minutos salvo `force`). Solo con un `completed` aún no visto lee
 *   `/line`: es el único momento en que `api` recalcula el nivel. Además, una vez por navegador y
 *   tenant, siembra el nivel conocido en la revisión con `force` del inicio de sesión.
 * - Marca `cr_line_done_seen_<id>` y el nivel conocido solo al abrir (o al decidir no abrir: línea
 *   castigada, ofertas sin subida o sin nivel guardado). Con un bloqueo (`hold`) no abre ni marca; el
 *   `release` del último motivo vuelve a revisar.
 * - Errores: silencio. Nunca toast.
 */
@Injectable({ providedIn: 'root' })
export class LineMilestoneWatcherService {
    static readonly THROTTLE_MS = 5 * 60 * 1000;

    readonly modal$ = new BehaviorSubject<LineMilestoneModal | null>(null);

    /** Reloj (los specs lo fijan). */
    clockNow: () => number = () => Date.now();

    private readonly holds = new Set<LineMilestoneHold>();
    private lastCheckAt: number | null = null;
    private generation = 0;
    private inFlight = false;
    /** Créditos ya revisados con la v2 apagada: no se relee `/line` por ellos hasta `reset()`. */
    private readonly v1Checked = new Set<string>();

    constructor(private creditos: CreditosService, private userService: UserService) {}

    get isOpen(): boolean {
        return !!this.modal$.value;
    }

    get isBlocked(): boolean {
        return this.holds.size > 0;
    }

    hold(reason: LineMilestoneHold): void {
        this.holds.add(reason);
    }

    /** Sin efecto si el motivo no estaba retenido; al quedar sin bloqueos vuelve a revisar. */
    release(reason: LineMilestoneHold): void {
        if (!this.holds.delete(reason)) return;
        if (!this.holds.size) this.check({ force: true });
    }

    check({ force = false }: { force?: boolean } = {}): void {
        const user = this.userService.user$.getValue();
        const tenantId: string | null = user?.tenantId ?? null;
        if (!user?.isOwner || !user?.creditAccess || !tenantId) return;
        if (this.isOpen || this.isBlocked || this.inFlight) return;
        const now = this.clockNow();
        if (!force && this.lastCheckAt !== null && now - this.lastCheckAt < LineMilestoneWatcherService.THROTTLE_MS) return;
        this.lastCheckAt = now;

        const generation = this.generation;
        const seed = force && readKnownLineLevel(tenantId) === null;
        this.inFlight = true;
        this.creditos
            .getCurrent$()
            .pipe(
                map((res: any) => (res?.data && !res.data.deleted ? (res.data as Credito) : null)),
                switchMap(credito => {
                    const pending = this.pendingCredito(credito);
                    if (!pending && !seed) return of(null);
                    return this.creditos.refreshLine$().pipe(map(data => ({ data, credito: pending })));
                }),
                catchError(() => of(null)),
                finalize(() => {
                    if (generation === this.generation) this.inFlight = false;
                }),
            )
            .subscribe(result => {
                if (result && generation === this.generation) this.resolve(result.data, result.credito, tenantId);
            });
    }

    /** X, «Ahora no», fondo o «Entendido». Ya se marcó al abrir. */
    close(): void {
        this.modal$.next(null);
    }

    /** Logout: descarta la revisión en vuelo y limpia el modal, los bloqueos y el tope. */
    reset(): void {
        this.generation++;
        this.inFlight = false;
        this.lastCheckAt = null;
        this.holds.clear();
        this.v1Checked.clear();
        this.modal$.next(null);
    }

    /** Un crédito completado que el vigía todavía no resolvió. */
    private pendingCredito(credito: Credito | null): Credito | null {
        const id = credito?._id;
        if (credito?.status !== 'completed' || !id || isLineDoneSeen(id) || this.v1Checked.has(id)) return null;
        return credito;
    }

    private resolve(data: Parameters<typeof CreditosService.lineOf>[0], credito: Credito | null, tenantId: string): void {
        const line = CreditosService.lineOf(data);
        if (!line) {
            // v2 apagada: no se marca (puede encenderse), pero no se relee `/line` por este crédito.
            if (credito?._id) this.v1Checked.add(credito._id);
            return;
        }
        const known = readKnownLineLevel(tenantId);
        if (!credito) {
            if (known === null) writeKnownLineLevel(tenantId, line.level);
            return;
        }
        if (credito.origin === 'line') {
            if (isLineWrittenOff(line)) {
                this.settle(credito._id, tenantId, line.level);
                return;
            }
            this.open({ line, credito }, credito._id, tenantId);
            return;
        }
        // Crédito de ofertas completado con la v2 encendida: solo la subida de nivel que trae.
        if (known !== null && line.level > known) {
            this.open({ line, credito: { level: known } }, credito._id, tenantId);
            return;
        }
        this.settle(credito._id, tenantId, line.level);
    }

    /** Abre y marca, salvo que otro modal ya esté encima: entonces no marca y el `release` revisa. */
    private open(modal: LineMilestoneModal, creditoId: string, tenantId: string): void {
        if (this.isOpen || this.isBlocked) return;
        this.modal$.next(modal);
        this.settle(creditoId, tenantId, modal.line.level);
    }

    private settle(creditoId: string, tenantId: string, level: number): void {
        markLineDoneSeen(creditoId);
        writeKnownLineLevel(tenantId, level);
    }
}
